import { Box, Dialog, Typography } from '@mui/material';
import { Button } from '@mui/material';

import DeactivateIcon from 'media/images/icons/account-cancel-outline.svg?react';
import ActivateIcon from 'media/images/icons/account-reactivate-outline.svg?react';

import { UserActivationActionType } from '../types';

// Modal data based on the ACTION being performed
const userActivationDataDialog = {
  [UserActivationActionType.Activate]: {
    buttonColor: 'primary' as const,
    buttonLabel: 'Activate',
    icon: <ActivateIcon />,
    subtitle: 'This will restore their account access.',
    title: 'Reactivate User Account?',
  },
  [UserActivationActionType.Deactivate]: {
    buttonColor: 'error' as const,
    buttonLabel: 'Disable',
    icon: <DeactivateIcon />,
    subtitle: `This will immediately revoke the user's access, they will not be able to login or access any content. 
          This action can be reversed by reactivating the user account.`,
    title: 'Disable User Account?',
  },
};

interface UserActivationConfirmModalProps {
  isFetching?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  userActivation: UserActivationActionType;
}

export const UserActivationConfirmModal = ({
  onClose,
  userActivation,
  onConfirm,
  isFetching = false,
}: UserActivationConfirmModalProps) => {
  const dialogData = userActivationDataDialog[userActivation];

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      onClose={isFetching ? undefined : onClose}
      open={true}
      PaperProps={{
        sx: {
          alignSelf: 'flex-start',
          margin: '32px',
        },
      }}
    >
      <Box sx={{ padding: '72px' }}>
        <Box
          alignItems="center"
          display="flex"
          flexDirection="column"
          gap={2}
          textAlign="center"
        >
          <Box
            aria-hidden="true"
            sx={{
              alignItems: 'center',
              display: 'flex',
              height: '32px',
              justifyContent: 'center',
              width: '32px',
            }}
          >
            <Box
              sx={{
                '& svg': {
                  height: '32px',
                  width: '32px',
                },
              }}
            >
              {dialogData.icon}
            </Box>
          </Box>

          <Typography
            fontWeight="bold"
            id="user-activation-dialog-title"
            variant="h6"
          >
            {dialogData.title}
          </Typography>

          <Typography
            color="text.secondary"
            id="user-activation-dialog-description"
            variant="subtitle1"
          >
            {dialogData.subtitle}
          </Typography>

          <Box
            display="flex"
            gap={2}
            justifyContent="center"
            mt={2}
            width="100%"
          >
            <Button
              disabled={isFetching}
              onClick={onClose}
              sx={{ minWidth: 100 }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              color={dialogData.buttonColor}
              disabled={isFetching}
              onClick={onConfirm}
              sx={{ minWidth: 100 }}
              variant="contained"
            >
              {dialogData.buttonLabel}
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};
